/**
 * toolbox-activity.ts
 * Copyright (c) 2017, My Coverage Plan, Inc.
 * All rights reserved.
 */

import { AbstractControl } from "@angular/forms";

import { TypedDataHolder } from "../common/data/typed-data";
import { ActivityProgress, TimingData }
    from "../server/messages/aggregation/activity-progress";
import { Message, ReplyVerifyingMessage }
    from "../server/messages/general/";

import { ConstantNode, FilterNode, OperatorNode, UndefinedNode, VariableNode }
    from "./filter-node";
import { Equals, Is, Operators } from "./operators";
import { ToolSchema } from "./schemas";
import { AggVariable } from "./typed-parameter";

export enum ActivityState
{
    Configuration,
    Scheduled,
    Running,
    Complete,
    Paused,
    Aborted,
    Released
}
const ACTIVITY_STATE_NAMES : Map<ActivityState, string> = new Map([
    [ActivityState.Configuration, "Configuration"],
    [ActivityState.Scheduled, "Scheduled"],
    [ActivityState.Running, "Running"],
    [ActivityState.Complete, "Complete"],
    [ActivityState.Paused, "Paused"],
    [ActivityState.Aborted, "Aborted"]
]);

export interface FilterOptions
{
    type? : "simple" | "advanced";
}

export type ActivityType = "aggregation" | "data-export";

export type BasicActivityCommand = "cancel" | "pause" | "resume" | "status";

/**
 * A {@link ToolboxActivity} is the superclass for server "tasks" created by
 * analysis tools that have a common set of states, various in-progress and
 * final counts (including exclusions by viability or user filter), and timing
 * stats for local and server performance.
 */
export abstract class ToolboxActivity
{
    public id : number;

    public title : string = "Unknown";

    public algorithm? : string;
    public group? : string;

    public population : number;
    public populationIsEstimated : boolean;
    public remaining : number;

    public viable : number;
    public unanswered : number;

    public processed : number;
    public rejected : number;

    public unknownCount : number;

    public serverStart : Date;
    public serverEnd : Date;
    public serverSeconds : number;
    public localStart : Date;
    public localEnd : Date;
    public localSeconds : number;

    public configurationControls : AbstractControl[] = [];

    public state : ActivityState = ActivityState.Configuration;
    public get readableState () : string
    {
        return ACTIVITY_STATE_NAMES.get(this.state);
    }

    public get isScheduled () : boolean
    {
        return this.state === ActivityState.Scheduled;
    }
    public get isStarted () : boolean
    {
        return this.state > ActivityState.Configuration;
    }
    public get isRunning () : boolean
    {
        return this.state === ActivityState.Running;
    }
    public get isPaused () : boolean
    {
        return this.state === ActivityState.Paused;
    }
    public get isComplete () : boolean
    {
        return this.state === ActivityState.Complete;
    }

    public clearProgress () : void
    {
        this.population = 1;
        this.remaining = 1;
        this.populationIsEstimated = true;
        this.viable = 0;
        this.unanswered = 0;
        this.processed = 0;
        this.rejected = 0;
        this.unknownCount = 0;

        this.serverStart = null;
        this.serverEnd = null;
        this.serverSeconds = 0;
        this.localStart = null;
        this.localEnd = null;
        this.localSeconds = 0;
    }

    public abstract clearResults () : void;
    public abstract readSchema (schema : ToolSchema) : void;

    public markScheduled () : any
    {
        this.clearProgress();
        this.clearResults();
        this.configurationControls.forEach(
            (control : AbstractControl) => control.disable());
        if (this.filter)
        {
            this.filterString = this.filter.toReadable();
        }

        // Scheduled tasks are immediately considered to be running, so we
        // advance directly to the "running" state. NOTE: This will change when
        // the server is capable of scheduling non-immediate activities and
        // running them detached from user sessions.
        this.markStarted();
    }

    protected markStarted () : void
    {
        this.state = ActivityState.Running;
        this.localStart = new Date();
    }

    protected static EXP_SCALE : number = -0.333;
    protected estimatePopulation () : number
    {
        return Math.max(
            (this.processed + this.rejected + this.unanswered)
            * this.scalingFactor(),
            1);
    }

    protected scalingFactor () : number
    {
        if (this.localSeconds)
        {
            return (1 /
                (1 - Math.min(
                    Math.exp(ToolboxActivity.EXP_SCALE * this.localSeconds),
                    0.8
                )));
        }
        else
        {
            return 2.0;
        }
    }

    public readProgress (progress : ActivityProgress) : void
    {
        this.processed = progress.processed;
        this.rejected = progress.rejected;
        this.viable = progress.viable;

        if ("unanswered" in progress)
        {
            this.unanswered = progress.unanswered;
        }

        if (this.localStart)
        {
            this.localSeconds = (Date.now() - this.localStart.valueOf()) / 1000;
        }

        if ("population" in progress)
        {
            this.populationIsEstimated = false;
            this.population = progress.population;
        }
        else
        {
            this.population = this.estimatePopulation();
        }
        this.remaining = this.population
            - (this.processed + this.rejected + this.unanswered);
    }

    public abstract readMessageReport (m : Message) : void;

    public readCompletion (progress : ActivityProgress & TimingData)
        : void
    {
        this.state = ActivityState.Complete;

        // Record final progress values
        this.processed = progress.processed;
        this.rejected = progress.rejected + progress.emptyRejectedCount;
        this.viable = progress.viable;
        if ((this.processed + this.rejected) !== this.viable)
        {
            this.unknownCount = this.viable - (this.processed + this.rejected);
            console.error(
                "At task end, viable did not equal processed + rejected",
                this.unknownCount);
        }
        if ("unanswered" in progress)
        {
            this.unanswered = progress.unanswered;
        }
        this.populationIsEstimated = false;
        if ("population" in progress)
        {
            this.population = progress.population;
            // TODO: check for discrepancies
        }
        else
        {
            this.population = this.viable + this.unanswered;
        }
        this.remaining = 0;

        // Record the timing data provided by the server
        this.serverStart = new Date(progress.start);
        this.serverEnd = new Date(progress.ended);
        this.serverSeconds = progress.duration / 1000;
        // Record local timing data
        this.localEnd = new Date();
        this.localSeconds =
            (this.localEnd.valueOf() - this.localStart.valueOf()) / 1000;
    }

    public filter : FilterNode = null;
    public filterString : string;
    public filterOptions : AggVariable[] = [];

    public clearFilter () : void
    {
        this.filter = null;
        // console.warn(this, this.filter);
    }

    public initDefaultFilter (options? : FilterOptions) : void
    {
        const isSimple : boolean = (options && options.type === "simple");
        const eqNode : OperatorNode = OperatorNode.BasicEquality(
            this.filterOptions as TypedDataHolder[], !isSimple);

        if (isSimple)
        {
            this.filter = new OperatorNode(
                Operators.get("all of"),
                [],
                true,
                this.filterOptions as TypedDataHolder[]
            );
            (this.filter as OperatorNode).addChild(0, eqNode);
        }
        else
        {
            this.filter = eqNode;
        }
    }

    public abstract requestFor (action : BasicActivityCommand)
        : ReplyVerifyingMessage;
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/aggregation/toolbox-activity.ts